import React from "react";
import { Button } from "@/components/ui/button";
import { WarningOctagon, ArrowClockwise } from "@phosphor-icons/react";

/**
 * Top-level error boundary. Wrap once around the entire app tree.
 *
 * Catches any uncaught render error and replaces the "white screen of death"
 * with a branded recovery card. Without this, a single unhandled exception
 * in any component blanks the entire page and looks like the app crashed.
 *
 * Pure recovery UI only — no logging service yet (Sentry comes later).
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Keep this so users / support can give us a useful screenshot.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info?.componentStack);
  }

  reset = () => {
    this.setState({ hasError: false, error: null });
  };

  reload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const msg = this.state.error?.message || "Unexpected error";
    return (
      <div
        className="min-h-screen grid place-items-center bg-stone-50 px-6"
        data-testid="error-boundary-fallback"
      >
        <div className="max-w-md w-full bg-white border border-stone-200 rounded-lg p-8 shadow-sm">
          <div className="h-12 w-12 rounded-full bg-amber-50 text-amber-700 grid place-items-center mb-5">
            <WarningOctagon size={26} weight="duotone" />
          </div>
          <div className="text-xs uppercase tracking-[0.2em] text-stone-500">
            Alaine's Bench
          </div>
          <h1 className="text-2xl font-bold text-stone-900 mt-1">
            Something went sideways.
          </h1>
          <p className="text-stone-600 mt-3 text-sm leading-relaxed">
            The page hit an unexpected error. Your saved work is safe — it lives
            on our servers, not in the page. Try reloading, or head back to your
            dashboard.
          </p>
          {msg && (
            <pre
              className="mt-4 text-[11px] text-stone-500 bg-stone-50 border border-stone-200 rounded p-2 overflow-x-auto"
              data-testid="error-boundary-detail"
            >
              {msg}
            </pre>
          )}
          <div className="flex flex-wrap gap-2 mt-6">
            <Button
              onClick={this.reload}
              className="bg-emerald-800 hover:bg-emerald-700 text-white"
              data-testid="error-boundary-reload"
            >
              <ArrowClockwise size={15} weight="bold" className="mr-1.5" />
              Reload page
            </Button>
            <Button
              onClick={() => {
                this.reset();
                window.location.assign(`${process.env.PUBLIC_URL || ""}/dashboard`);
              }}
              variant="outline"
              className="border-stone-300"
              data-testid="error-boundary-home"
            >
              Back to dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
